import get from 'lodash/get';

// Artifactory
import {toasterActions} from '@homeaway/react-toaster';

// Relative Imports
import listingActions from '../actions/listingActions';
import listingExtendedActions from '../actions/listingExtendedActions';
import EdapActions from '../actions/edapActions';
import RouterActions from '../actions/routerActions';
import {writeCookie, TRIP_COOKIE} from '../utils/cookieHelpers';
import {OpenModalStates} from '../records/listingPageRecord';
import {log} from '../api/logger';
import {getRecommendedProperties} from '../api/recommendedProperties';
import {getRegistrationNumber} from '../api/registrationNumber';
import {fetchGeoDistance} from '../api/geoDistance';
import {availableRoutes} from '../routes';
import {logPriceSummaryEvent as logPriceSummaryEventUtil} from './utils/logPriceSummaryEvent';
import {scrollToNavTab} from '../utils/scrollToNavTab';
import {isInBucket} from '../utils/abacusHelper';

/* eslint-disable complexity, no-bitwise */
function setModalOpenStatus(store, isOpen, modalState) {
    const currentState = store.getState().listingPageReducer.openModalState;
    const validClosingStates = [
        OpenModalStates.CIVIL_CODE_DISCLAIMER,
        OpenModalStates.LOGIN_EXPERIENCE,
        OpenModalStates.INQUIRY_LOGIN_EXPERIENCE,
        OpenModalStates.REQUEST_TO_BOOK,
        OpenModalStates.INQUIRY,
        OpenModalStates.MAP,
        OpenModalStates.DESCRIPTION,
        OpenModalStates.UNIT_DESCRIPTION,
        OpenModalStates.REVIEWS,
        OpenModalStates.AMENITY,
        OpenModalStates.OWNER_PROFILE,
        OpenModalStates.LOCATION,
        OpenModalStates.SEARCH,
        OpenModalStates.SEARCH_DATE_PICKER,
        OpenModalStates.SEARCH_GUEST_PICKER,
        OpenModalStates.VIRTUAL_TOUR
    ];
    const validOpenFromStates = [
        OpenModalStates.NONE,
        OpenModalStates.CIVIL_CODE_DISCLAIMER,
        OpenModalStates.REQUEST_TO_BOOK,
        OpenModalStates.INQUIRY,
        OpenModalStates.MAP,
        OpenModalStates.DESCRIPTION,
        OpenModalStates.UNIT_DESCRIPTION,
        OpenModalStates.REVIEWS,
        OpenModalStates.OWNER_PROFILE,
        OpenModalStates.AMENITY,
        OpenModalStates.LOCATION,
        OpenModalStates.SEARCH,
        OpenModalStates.INQUIRY_LOGIN_EXPERIENCE
    ];
    let newState = currentState;

    if (!isOpen && !!~validClosingStates.indexOf(currentState) && currentState === modalState) {
        newState = OpenModalStates.NONE;
    } else if (isOpen && !!~validOpenFromStates.indexOf(currentState)) {
        // We can probably get away with just checking isOpen, and force
        // into this dialog, but I am keeping the extra check above in place
        // for now.
        // The possible issues are when the other dialogs have closed, but
        // the state was not updated.  It points to bad handlers, and
        // inspecting the store will show the wrong enum state.
        newState = modalState;
    }
    store.dispatch(listingActions.updateModalState(newState));

    // React-modal sucks... so clean up left over classes and data.
    // This is a ~hack~, and will be deleted once the non-modal stuff is in.
    // Because of that, I am adding a bunch typeof checks.
    /* istanbul ignore next */
    if (newState === OpenModalStates.NONE) {
        if (typeof window !== 'undefined' && typeof window.document !== 'undefined') {
            if (window.document.body && window.document.body.dataset) {
                window.document.body.dataset.reactModals = JSON.stringify({});
            }
        }
    }
}
/* eslint-enable complexity, no-bitwise */

function setRequestToBookModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.REQUEST_TO_BOOK);
}

function processServerSideEvaluatedTests(store) {
    const buckettedInTests = store.getState().systemReducer.serverSideTestBuckets;

    buckettedInTests.forEach((testName) => {
        store.dispatch(EdapActions.logTestExposure(testName));
    });
}

function setInquiryModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    if (!isOpen) {
        store.dispatch(listingActions.resetInquiryState());
    }
    setModalOpenStatus(store, isOpen, OpenModalStates.INQUIRY);
}

function setCivilCodeDisclaimerModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.CIVIL_CODE_DISCLAIMER);
}

function setLoginModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.LOGIN_EXPERIENCE);
}

function setInquiryLoginModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.INQUIRY_LOGIN_EXPERIENCE);
}

function setDescriptionModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.DESCRIPTION);
}

function setUnitDescriptionModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.UNIT_DESCRIPTION);
}

function setAmenityModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.AMENITY);
}

function setOwnerProfileModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.OWNER_PROFILE);
}

function setLocationModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.LOCATION);
}

function setMapModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    if (!isOpen) {
        store.dispatch(listingActions.resetMapState());
    }

    setModalOpenStatus(store, isOpen, OpenModalStates.MAP);
}

function setReviewsModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;
    setModalOpenStatus(store, isOpen, OpenModalStates.REVIEWS);
}

function setSearchModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.SEARCH);
}

function setSearchDatePickerModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.SEARCH_DATE_PICKER);
}

function setSearchGuestPickerModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.SEARCH_GUEST_PICKER);
}

function setVirtualTourModalOpen(store, action = {payload: {}}) {
    const {isOpen} = action.payload;

    setModalOpenStatus(store, isOpen, OpenModalStates.VIRTUAL_TOUR);
}

function updateTripDataCookie(store) {
    const tripData = store.getState().tripDetailsReducer;

    writeCookie(TRIP_COOKIE, {
        arrival: tripData.arriveDateText,
        departure: tripData.departDateText,
        numberOfAdults: tripData.adults,
        numberOfChildren: tripData.children,
        petIncluded: tripData.hasPets
    });
}

function recommendedPropertiesLogNoResults() {
    log({
        fetchRecommendedProperties: true,
        fetchRecommendedPropertiesHasResults: false,
        error: 'Recommended properties returned no results'
    });
}

// {listingId, site, fallBack, size, recommendationModel, startDate, endDate,
// minBeds, minBaths, minSleeps, maxRate, regionId, unitApiUrl}
function pageLoadedFetchRecommendedProperties(store) {
    const {listingReducer, systemReducer, tripDetailsReducer} = store.getState();
    // TODO replace this with await?
    return getRecommendedProperties({
        listingId: listingReducer.listingId,
        site: systemReducer.siteName,
        fallback: true,
        details: true,
        size: !systemReducer.arrivedFromExternalSite ? 10 : 4,
        source: 'PDP',
        startDate: tripDetailsReducer.arriveDateText,
        endDate: tripDetailsReducer.departDateText,
        regionId: listingReducer.regions && listingReducer.regions[0],
        searchTerm:
            listingReducer.geography &&
            listingReducer.geography.ids &&
            listingReducer.geography.ids[0] &&
            listingReducer.geography.ids[0].value,
        unitApiUrl: listingReducer.unitApiUrl
        // TODO: need region?
    })
        .then((response) => {
            if (!response.body) {
                store.dispatch(listingExtendedActions.fetchRecommendedPropertiesFailed());
                recommendedPropertiesLogNoResults();
                return;
            }

            const hasResults = !!(response.body.recommendedHits || []).length;
            if (!hasResults) {
                recommendedPropertiesLogNoResults();
            }

            store.dispatch(listingExtendedActions.fetchRecommendedPropertiesSuccess(response.body));
        })
        .catch((error) => {
            store.dispatch(listingExtendedActions.fetchRecommendedPropertiesFailed());

            // could not fetch recommended properties, do nothing
            log({
                fetchRecommendedProperties: false,
                error: error && error.response && error.response.body
            });
        });
}

// TODO replace this with await?
function pageLoadedFetchRegistrationNumber(store) {
    const {listingReducer} = store.getState();

    return getRegistrationNumber({listingId: listingReducer.listingId})
        .then((response) => {
            if (response.body && response.body.registrationNumber) {
                const {registrationNumber} = response.body;
                store.dispatch(
                    listingExtendedActions.fetchRegistrationNumberSuccess(registrationNumber)
                );
            }
        })
        .catch((error) => {
            log({
                fetchedRegistrationNumber: false,
                error: `getRegistrationNumber exception.  Error Message: ${error.message}`
            });
        });
}

function viewPriceDetailsClicked(store, action = {payload: {}}) {
    const {actionLocation} = action.payload;
    const clickedFromStickyBookingPanel = actionLocation === 'sticky booking panel';

    store.dispatch(
        EdapActions.triggerEdapEvent('generic.event', {
            eventcategory: 'pdp page',
            eventaction: clickedFromStickyBookingPanel
                ? 'sticky booking panel view details link click'
                : 'detailed price link click'
        })
    );

    store.dispatch(listingActions.setRequestToBookModalOpen(true));
}

function inquiryClicked(store, action = {payload: {}}) {
    const {systemReducer, travelerFeaturesReducer} = store.getState();
    const loggedIn = travelerFeaturesReducer.isLoggedIn;
    const isMobile = systemReducer.isMobile;
    const {actionLocation} = action.payload;

    if (isInBucket('ha_pdp_inquiry_login') && !loggedIn) {
        store.dispatch(listingActions.setInquiryLoginModalOpen(true));
        return;
    }

    if (isMobile) {
        store.dispatch(
            RouterActions.navigateTo(`${systemReducer.baseUrlPath}?r=${availableRoutes.INQUIRY}`)
        );
    } else {
        store.dispatch(listingActions.setInquiryModalOpen(true));
    }

    store.dispatch(
        EdapActions.triggerEdapEvent('generic.modal', {
            modalname: 'inquiry',
            modalaction: 'open',
            actionlocation: actionLocation
        })
    );
}

function civilCodeDisclaimerClicked(store, action = {payload: {}}) {
    const {systemReducer} = store.getState();
    const {isMobile} = systemReducer;
    const {actionLocation} = action.payload;

    if (isMobile) {
        store.dispatch(
            RouterActions.navigateTo(
                `${systemReducer.baseUrlPath}?r=${availableRoutes.CIVIL_CODE_DISCLAIMER}`
            )
        );
    } else {
        store.dispatch(listingActions.setCivilCodeDisclaimerModalOpen(true));
    }

    store.dispatch(
        EdapActions.triggerEdapEvent('generic.modal', {
            modalname: 'civil code disclaimer',
            modalaction: 'open',
            actionlocation: actionLocation
        })
    );
}

function navClicked(store, action = {payload: {}}) {
    const {tabName, largeNav} = action.payload;

    store.dispatch(
        EdapActions.triggerEdapEvent('listing.infotab.click', {
            tabname: tabName
        })
    );

    scrollToNavTab(tabName, 500, 0, largeNav ? 12 : 2);
}

function logPriceSummaryEvent(store, action = {payload: {}}) {
    logPriceSummaryEventUtil(store, action.payload.priceSummaryEventParams);
}

function triggerUrgencyToast(store, action = {payload: {}}) {
    const {element} = action.payload;

    store.dispatch(
        toasterActions.createToast({
            renderContent: element,
            type: 'inverse'
        })
    );
}

function pageLoadedFetchGeoDistance(store) {
    const state = store.getState();

    const params = {
        listingId: get(state, 'listingReducer.listingId'),
        locale: get(state, 'systemReducer.locale')
    };

    return fetchGeoDistance(params)
        .then((response) => {
            if (!response.body) {
                return;
            }

            store.dispatch(listingExtendedActions.fetchGeoDistanceSuccess(response.body));
        })
        .catch((error) => {
            log({
                fetchGeoDistance: false,
                error: `fetchGeoDistance exception.  Error Message: ${error.message}`
            });
        });
}

export default setRequestToBookModalOpen;
export {
    civilCodeDisclaimerClicked,
    recommendedPropertiesLogNoResults,
    setRequestToBookModalOpen,
    setCivilCodeDisclaimerModalOpen,
    setInquiryModalOpen,
    setLoginModalOpen,
    setInquiryLoginModalOpen,
    setDescriptionModalOpen,
    setUnitDescriptionModalOpen,
    setAmenityModalOpen,
    setLocationModalOpen,
    setOwnerProfileModalOpen,
    setSearchModalOpen,
    setSearchDatePickerModalOpen,
    setSearchGuestPickerModalOpen,
    setMapModalOpen,
    setReviewsModalOpen,
    pageLoadedFetchRecommendedProperties,
    pageLoadedFetchRegistrationNumber,
    pageLoadedFetchGeoDistance,
    processServerSideEvaluatedTests,
    updateTripDataCookie,
    viewPriceDetailsClicked,
    inquiryClicked,
    navClicked,
    logPriceSummaryEvent,
    setVirtualTourModalOpen,
    triggerUrgencyToast
};
